/**
 * System role helpers for CRC HR MVP RBAC.
 * DB code `dept_user` is displayed as "User"; legacy alias `user` normalizes to `dept_user`.
 */

/**
 * @param {string|object|null} role
 * @returns {string}
 */
export function normalizeSystemRole(role) {
  if (role == null) return '';
  const code = typeof role === 'string' ? role : role?.code;
  const key = String(code || '').trim().toLowerCase();
  if (key === 'user') return 'dept_user';
  return key;
}

/**
 * @param {Array<string|object>} roles
 * @returns {string[]}
 */
export function normalizeSystemRoles(roles) {
  if (!Array.isArray(roles)) return [];
  return roles.map(normalizeSystemRole).filter(Boolean);
}

/**
 * @param {Array<string|object>} roles
 * @param {string} code
 */
export function hasSystemRole(roles, code) {
  const want = normalizeSystemRole(code);
  return normalizeSystemRoles(roles).includes(want);
}

export function isSuperAdminRole(roles) {
  return hasSystemRole(roles, 'super_admin');
}

export function isDepartmentAdminRole(roles) {
  return hasSystemRole(roles, 'dept_admin');
}

/** Regular employee (dept_user / legacy user), not also an admin role. */
export function isRegularUserRole(roles) {
  if (isSuperAdminRole(roles) || isDepartmentAdminRole(roles)) return false;
  return hasSystemRole(roles, 'dept_user');
}

export function isAdminRole(roles) {
  return isSuperAdminRole(roles) || isDepartmentAdminRole(roles);
}

/** Ascending privilege order for employee create / account role pickers (User first). */
export const SYSTEM_ROLE_SELECT_ORDER = ['dept_user', 'dept_admin', 'super_admin'];

/** @type {Record<string, string>} */
export const SYSTEM_ROLE_DESCRIPTIONS = {
  dept_user: 'Self-service access to own profile and assigned workflows.',
  dept_admin: 'Manage employees and HR data within their department.',
  super_admin: 'Full access across all departments and system settings.'
};

/**
 * @param {string|object|null} role
 * @returns {string}
 */
export function getSystemRoleDescription(role) {
  const code = normalizeSystemRole(role);
  return SYSTEM_ROLE_DESCRIPTIONS[code] || '';
}

/**
 * @param {Array<{ id?: number, code?: string }>} roles
 * @returns {number|null}
 */
export function findDefaultSystemRoleId(roles) {
  const match = (roles || []).find((role) => normalizeSystemRole(role.code) === 'dept_user');
  const id = match?.id;
  return id == null ? null : Number(id);
}

/**
 * @param {Array<{ id?: number, code?: string, name?: string }>} roles
 * @returns {Array<{ id?: number, code?: string, name?: string, description: string }>}
 */
export function mapSystemRoleSelectOptions(roles) {
  const order = SYSTEM_ROLE_SELECT_ORDER;
  return [...(roles || [])]
    .sort((a, b) => {
      const ai = order.indexOf(normalizeSystemRole(a.code));
      const bi = order.indexOf(normalizeSystemRole(b.code));
      const ar = ai === -1 ? 999 : ai;
      const br = bi === -1 ? 999 : bi;
      if (ar !== br) return ar - br;
      return String(a.name || '').localeCompare(String(b.name || ''));
    })
    .map((role) => ({
      ...role,
      description: getSystemRoleDescription(role.code)
    }));
}
